<template>
    <div :id="rootElementId" :style="`height:${svgHeight}px`" class="lollipop-container"></div>
</template>

<script>
import * as d3v4 from "d3v4";
import { round, getDataUrl } from '../../utils/functions';
import { loadFonts, copyAndAppendElement } from '../../utils/exportToPNG';
import ResizeObserver from 'resize-observer-polyfill';
import UserService from '../../services/user.service';

export default {
  name: 'LollipopChart',
  props: {
    id:{
      type: String,
      required: true
    },
    size: {
      type: Number,
      required: false,
    },
    chartData: {
      type: Object,
      required: true,
    },
    radarTemplate:{
      type: String,
      default: ""
    },
    dataPer90MinText: {
      type: String,
      required: false,
    },
    adjustToPossessionText: {
      type: String,
      required: false,
    },
    playerName: {
      type: String,
      required: true,
    },
    radarTitle: {
      type: String,
      required: true,
    },
    yob: {
      type: String,
      required: true,
    },
    minMaxLabel:{
      type: String,
      default: ""
    },
    mediaMode:{
      type: Boolean,
      default: false
    },
    colors:{
      type: Object,
      required:false
    },
    bgColor:{
      type: String,
      required:false
    },
    textColor:{
      type: String,
      required:false
    },
    logoCfg:{
      type: String,
      required:false
    },
    fontFamily:{
      type: String,
      default: 'NeueMontreal'
    }
  },
  data() {
    return {
      observer: null,
      images: {},
    }
  },
  computed: {
    darkMode(){
      return this.$vuetify.theme.dark;
    },
    rootElementId(){
      return `lollipop${this.id}`
    },
    svgHeight() {
      const isMobileDevice = this.size < 440 ? 1 : 0;
      return isMobileDevice ? this.svgWidth : this.svgWidth * 0.73;
    },
    svgWidth() {
      const isMobileDevice = this.size < 440 ? 1 : 0;
      return isMobileDevice ? this.size * 0.9 : this.size * 0.95;
    },
  },
  async mounted() {
    this.drawChart('#' + this.rootElementId, this.chartData);
  },
  methods: {
    onResize(){
      let elto = document.getElementById(this.rootElementId);
      if(elto){
        this.$emit("sizeChange", {height:elto.clientHeight,width:elto.clientWidth});
      }
    },
    async drawChart(id, chartData){
      this.observer = new ResizeObserver(this.onResize).observe(document.getElementById(this.rootElementId));
      //Config data
      var cfg = {
        titleFontSize: 28, //Font size of the labels
        subtitleFontSize: 22, //Font size of the labels
        labelFontSize: 18, //Font size of the labels
        legendFontSize: 17, //Font size of the legend and categories
        legendOffsetY: 20, //Offset Y axis of the legend
        legendOffsetX: 100, //Offset X axis of the legend
        wrapWidthYLabel: 120, // Max Width for labels of Y axis
        spaceLabelAndAxis: 3, //Space between label and the path of Y axis
      };
      // Set the dimensions and margins of the graph
      const isMobileDevice = this.size < 440 ? 1 : 0;
      //1s duration transition between old radar and neew radar
      const duration = 1000;
      const margin = {
        top: isMobileDevice ? (this.size * 0.2) : (this.size * 0.08) > 110 ? 110 : (this.size * 0.08),
        right: isMobileDevice ? (this.size * 0.17) : (this.size * 0.13),
        bottom: isMobileDevice ? (this.size * 0.32) : (this.size * 0.06),
        left: isMobileDevice ? (this.size * 0.28) : (this.size * 0.24) > 200 ? 200 : (this.size * 0.24)
      };
      const CHART_WIDTH = this.svgWidth - margin.left - margin.right;
      const CHART_HEIGHT = isMobileDevice ? this.svgHeight : this.svgHeight - margin.top - margin.bottom;
      const sizePerStat = (CHART_HEIGHT - margin.bottom) / chartData.statDTOs[0].length;
      const ratio = this.svgWidth <= 380 ? 0.0025 : this.svgWidth < 700 ? 0.0017 : 0.0015;
      const CHART_SIZE_RATIO = (this.svgWidth/2) * ratio;
      //Driblab Logo Size
      const logoWidth = this.svgWidth * 0.2;
      const logoHeight = this.svgHeight * 0.1;
      //Legend
      const quintiles = [{value:"0-19", color: "#F64030"},
                      {value:"20-39", color: "#FBA180"},
                      {value:"40-59", color: "#FCE230"},
                      {value:"60-79", color: "#73B240"},
                      {value:"80-100", color: "#476F20"}];

      var playerChartData = chartData.statDTOs[0];
      let radarPosition = chartData.radarPosition;
      let numberOfPlayers = chartData.numberOfPlayers;

      //Order all stats by category
      playerChartData.sort((a,b) => (a.categoryOrder > b.categoryOrder) ? 1 : ((b.categoryOrder > a.categoryOrder) ? -1 : 0));
      var allStats = playerChartData.map((stat) => {
        //If stat is reverse, then ranking = numberOfPlayers - ranking
        let ranking = stat.isReverse ? (numberOfPlayers - (stat.ranking - 1)) : stat.ranking;
        //Main Player percentile value
        let percentileValue = numberOfPlayers > 0 ? (1 - (ranking / numberOfPlayers)) + 0.01 : 0.01;
        percentileValue = percentileValue < 0.01 ? 0.01 : percentileValue > 1 ? 1 : percentileValue;
        let valueToShow = (stat.valueToShow > 10 ? round(stat.valueToShow, 1) : round(stat.valueToShow, 2));
        //If value is 0 and is a Ratio Stat (statTypeId = 3), set - as value to show
        if(!valueToShow && stat.statTypeId == 3){
          valueToShow = "-";
        }

        return {
          code: stat.code,
          label: (stat.isAdjustedToPossession ? "*" : "") + this.$t('statsNaming.' + stat.code),
          percentileValue: Math.trunc(percentileValue * 100),
          isPercentage: stat.isPercentage,
          category: stat.category,
          ranking: ranking,
          valueToShow: valueToShow,
          tooltip: this.$t('statsNaming.description.' + stat.code)
        }
      });

      let statsByCategories = playerChartData.reduce((accum, stat) => {
        let categoryArray = _.remove(accum, (o) => o.category === stat.category);
        if(categoryArray && categoryArray.length){
          let categoryObj = categoryArray[0];
          categoryObj.total++;
          accum.push(categoryObj);
        }else{
          accum.push({category: stat.category, total: 1, percentileValue: stat.percentileValue});
        }

        return accum;
      },[]);

      //Remove all tooltips
      d3v4.selectAll(".tooltip").remove();
      // Define the div for the tooltip
      var tooltip = d3v4.select("body").append("div")
        .attr("class", "tooltip")
        .style("z-index", "9999")
        .style("display", "none");

      //Recover lines and dots from previous SVG
      var prevSVG = d3v4.select(id).select('svg'),
      circles = [],
      lines = [],
      values = [];
      //MAGIC FOR MAKE TRANSITIONS
      if (prevSVG.node()) {
        prevSVG.selectAll(".lollipopCircles").each(function(){
          circles.push(d3v4.select(this).attr("cx"));
        });
        prevSVG.selectAll(".lollipopLines").each(function(){
          lines.push(d3v4.select(this).attr("x2"));
        });
        prevSVG.selectAll(".lollipopValues").each(function(){
          values.push(d3v4.select(this).attr("x"));
        });
        //Remove previous SVG
        prevSVG.remove();
      }
      //END MAGIC

      //-----------------
      // Initialize SVG
      //----------------

      //Initiate the Lollipop chart SVG
      var svg = d3v4.select(id)
        .append("svg")
          .attr("id",`${this.rootElementId}SVG`)
          .attr("width", this.svgWidth)
          .attr("height", this.svgHeight)
          .style("background", this.bgColor)
        .append("g")
          .attr("transform", "translate(" + margin.left + "," + margin.top + ")");


      //------------------------------------
      // Draw Title and Number of Players
      //-----------------------------------

      let titleText = svg.append("text")
        .attr("x", isMobileDevice ? this.svgWidth/5 : CHART_WIDTH/2)
        .attr("y", isMobileDevice ? -((margin.top + 70) * CHART_SIZE_RATIO) : -margin.top/2 - (5 * CHART_SIZE_RATIO))
        .attr("text-anchor", "middle")
        .style("font-size", (7 * CHART_SIZE_RATIO) + "px")
        .style("font-family", this.fontFamily)
        .style("font-weight", "bold")
        .style("color", this.textColor)
        .style("fill",'currentColor')
        .text(this.playerName);

      //Do transition for PlayerName
      titleText.transition().duration(duration).style("font-size", (cfg.titleFontSize * CHART_SIZE_RATIO) + "px");
      //Add YOB to playerName
      let titleYOB = titleText.append("tspan")
        .attr("dx", 0)
        .attr("dy", -(2 * CHART_SIZE_RATIO))
        .style("font-size", (7 * CHART_SIZE_RATIO) + "px")
        .style("font-weight", "normal")
        .text(this.yob ? " ('" + this.yob + ")" : ""); //Last 2 digits of Player's Year of Birth

      //Do transition for Year of Birth
      titleYOB.transition().duration(duration).style("font-size", (20 * CHART_SIZE_RATIO) + "px");

      //Write Team and Season (or Seasons) text
      let seasonText = svg.append("text")
        .attr("x", isMobileDevice ? this.svgWidth/5 : CHART_WIDTH/2)
        .attr("y", isMobileDevice ? -((margin.top + 50) * CHART_SIZE_RATIO) : -margin.top/2 + (20 * CHART_SIZE_RATIO))
        .attr("text-anchor", "middle")
        .style("font-size", (7 * CHART_SIZE_RATIO) + "px")
        .style("font-family", this.fontFamily)
        .style("color",this.textColor)
        .style("fill",'currentColor')
        .html(this.radarTitle + " · " + chartData.minutesPlayed + " mins.");

      //Do transition for Season Text
      seasonText.transition().duration(duration).style("font-size", (cfg.subtitleFontSize * CHART_SIZE_RATIO) + "px");

      if(numberOfPlayers > 0){
        let leagueName = " · " + this.minMaxLabel;
        //Write top right number of position player (E.g: 152 Midfielders)
        svg.append("text")
          .attr("x", isMobileDevice ? (this.svgWidth - margin.left) : (this.svgWidth - margin.left) - 10)
          .attr("y", isMobileDevice ? -(margin.top * CHART_SIZE_RATIO) : 0)
          .attr("text-anchor", "end")
          .style("font-size", (cfg.subtitleFontSize * CHART_SIZE_RATIO) + "px")
          .style("font-family", this.fontFamily)
          .style("color", this.darkMode ? "#FFFFFF" : "gray")
          .style("font-weight", "bold")
          .style("fill",'currentColor')
          .text(numberOfPlayers + " " + this.$t("positions." + radarPosition + "s") + leagueName);

      }

      //---------------------
      // Draw Lollipop Chart
      //---------------------

      //Gray Dashed Lines (For Categories)
      svg.selectAll("myline")
        .data(statsByCategories)
        .enter()
        .append("line")
          .attr("x1", - margin.left)
          .attr("x2", "0")
          .attr("y1", (category, i) => {
            let previousTotal = 0;
            for(let j = 0; j < i; j++){
              previousTotal += statsByCategories[j].total;
            }
            let yCoord = isMobileDevice ? ((previousTotal * 1.1) * sizePerStat) - 20 : (previousTotal * sizePerStat);
            return yCoord;
          })
          .attr("y2", (category, i) => {
            let previousTotal = 0;
            for(let j = 0; j < i; j++){
              previousTotal += statsByCategories[j].total;
            }
            let yCoord = isMobileDevice ? ((previousTotal * 1.1) * sizePerStat) - 20 : (previousTotal * sizePerStat);
            return yCoord;
          })
          .attr("stroke", this.darkMode ? "#BCB6B6" : "gray")
          .attr("stroke-width", "2")
          .style("stroke-dasharray", ("10, 5"));

      //Write Categories (text over dashed lines)
      svg.selectAll("myline")
        .data(statsByCategories)
        .enter()
        .append("text")
        .attr("x", - margin.left)
        .attr("y", (cat, i) => {
          let offset = isMobileDevice ? 22 : 3;
          let previousTotal = 0;
          for(let j = 0; j < i; j++){
            previousTotal += statsByCategories[j].total;
          }
          let yCoord = isMobileDevice ? ((previousTotal * 1.1) * sizePerStat) - offset : (previousTotal * sizePerStat) - offset;
          return yCoord;
        })
        .attr("text-anchor", "start")
        .style("font-size", (cfg.legendFontSize * CHART_SIZE_RATIO) + "px")
        .style("font-family", this.fontFamily)
        .style("color", this.darkMode ? "#BCB6B6" : "#616161")
        .style("fill",'currentColor')
        .html((cat) => { return this.$t("faq.metrics.categories." + cat.category)});

      // Create scale for X axis
      const scaleX = d3v4.scaleLinear()
        .domain([0, 100])
        .range([ 0, CHART_WIDTH]);

      const axisX = d3v4.axisBottom(scaleX).tickFormat(function(d,i) {return "p" + d});

      svg.append("g")
        .attr("class", this.darkMode ? "axisDark" : "")
        .attr("transform", "translate(0," + (CHART_HEIGHT - margin.bottom) + ")")
        .call(axisX)
        .style("font-size", (cfg.labelFontSize * CHART_SIZE_RATIO) + "px")
        .style("font-family", this.fontFamily);

      // Create scale for Y axis
      let axisYrange = isMobileDevice ? [-20, (this.svgHeight * CHART_SIZE_RATIO) + margin.top + 2] : [0, CHART_HEIGHT - margin.bottom];
      const scaleY = d3v4.scaleBand()
        .range(axisYrange)
        .domain(allStats.map(function(d) { return d.label; }));

      const axisY = d3v4.axisLeft(scaleY).tickSizeOuter(0).tickSize(0);

      svg.append("g")
        .attr("class", this.darkMode ? "axisDark" : "")
        .attr("transform", "translate(0,0)")
        .call(axisY)
        .style("font-size", (cfg.labelFontSize * CHART_SIZE_RATIO) + "px")
        .style("font-family", this.fontFamily)
        .selectAll(".tick text")
        .attr("x", isMobileDevice ? -(cfg.spaceLabelAndAxis * 0.6) : -cfg.spaceLabelAndAxis)
        .call(this.wrap, isMobileDevice ? (cfg.wrapWidthYLabel * 0.6) : cfg.wrapWidthYLabel);

      // Lines
      svg.selectAll("lollipopLines")
        .data(allStats)
        .enter()
        .append("line")
        .attr("class", "lollipopLines")
        .attr("x1", 1)
        .attr("x2", function(d, i) {
          if(lines[i]){
            return lines[i];
          }
          return scaleX(d.percentileValue);
        })
        .attr("y1", function(d) { return scaleY(d.label) + (sizePerStat/2); }) //+ 0.5 to set Line at the same y coord that the axis tick
        .attr("y2", function(d) { return scaleY(d.label) + (sizePerStat/2); }) //+ 0.5 to set Line at the same y coord that the axis tick
        .attr("stroke", (d) => { return this.getColorPercentile(d.percentileValue);})
        .attr("stroke-width", (7 * CHART_SIZE_RATIO))
        .transition()
        .duration(duration)
        .attr("x2", function(d) { return scaleX(d.percentileValue); });

      var self = this;
      // Circles
      svg.selectAll("lollipopCircles")
        .data(allStats)
        .enter()
        .append("circle")
        .attr("class", "lollipopCircles")
        .attr("cx", function(d, i) {
          if(circles[i]){
            return circles[i];
          }
          return scaleX(d.percentileValue);
        })
        .attr("cy", function(d) { return scaleY(d.label) + (sizePerStat/2);})
        .attr("r", (9 * CHART_SIZE_RATIO))
        .attr("stroke", (d) => { return this.getColorPercentile(d.percentileValue);})
        .style("fill", (d) => { return this.getColorPercentile(d.percentileValue);})
        .on('mouseover',function(stat, i) {
            let widthTooltip = 350;
            let lines = Math.ceil(self.measureText(stat.tooltip) / widthTooltip) + 1;
            let yOffset = lines > 4 ? 0 : 20;
            let tooltipY = (event.pageY - lines * 22) - yOffset; // - (heightTooltip - (isMobileDevice * 5));//event.pageY - (heightTooltip + (isMobileDevice * 5));
            if(tooltipY < 0){
             //Hide bottom arrow
             d3.select("body > .tooltip").attr("class",'tooltip no-arrow');
             tooltipY = event.pageY + 8;
            }else{
             //Set bottom arrow
             d3.select("body > .tooltip").attr("class",'tooltip');
            }

            let leftOffset = (9 * CHART_SIZE_RATIO);
            d3v4.select("body > .tooltip")
              .transition()
              .duration(200)
              .style("display", "inline-block")
              .style("opacity", 0.95)
              .style("width", widthTooltip + "px")
              .style("height", "auto");

            d3v4.select("body > .tooltip")
              .style("font-size", (17 * CHART_SIZE_RATIO) + "px")
              .style("padding-top", (10 * CHART_SIZE_RATIO) + "px")
              .style("left", this.getBoundingClientRect().left - (widthTooltip/2) + leftOffset + "px")
              .style("top", tooltipY + "px")
              .html("<b>" + stat.label + "</b></br>" + stat.tooltip);

            //Increase circle radius on hover
            d3v4.select(this)
              .transition()
              .duration(300)
              .style("cursor", "pointer")
              .attr('r', (13 * CHART_SIZE_RATIO));

          })
          .on('mouseout',function() {
            //Recover nornmal radius after mouseout
            d3v4.select(this)
              .transition()
              .duration(200)
              .attr('r', (9 * CHART_SIZE_RATIO));

            d3v4.select("body > .tooltip").transition()
              .duration(300)
              .style("opacity", 0);

          })
          .transition()
          .duration(duration)
          .attr("cx", function(d) { return scaleX(d.percentileValue); });

      // Value next to Circles
      let circleValues = svg.selectAll("lollipopValues")
        .data(allStats)
        .enter()
        .append("text")
          .attr("class", "lollipopValues")
          .attr("x", function(d, i) {
            if(values[i]){
              return values[i];
            }
            return scaleX(d.percentileValue) + 10;
          })
          .attr("y", function(d) { return scaleY(d.label) + (sizePerStat/2) + (2/CHART_SIZE_RATIO);})
          .style("font-size", (cfg.labelFontSize * CHART_SIZE_RATIO) + "px")
          .style("font-weight", "bold")
          .style("font-family", this.fontFamily)
          .style("color",this.textColor)
          .style("fill",'currentColor')
          .html((stat) => { return stat.valueToShow + (stat.isPercentage ? "%" : "") });

      //Do transition for values close to circles
      circleValues
      .transition()
      .duration(duration)
      .attr("x", function(d) {
        return scaleX(d.percentileValue) + 10;
      });

      if(numberOfPlayers > 0){
        // Right text #Position (Percentile) - E.g: #1 (p70)
        svg.selectAll("lollipopCircles")
          .data(allStats)
          .enter()
          .append("text")
            .attr("x", isMobileDevice ? (this.svgWidth - margin.left) : (this.svgWidth - margin.left) - 10)
            .attr("y", function(d) { return scaleY(d.label) + (28 * CHART_SIZE_RATIO);})
            .attr("text-anchor", "end")
            .style("font-size", (cfg.labelFontSize * CHART_SIZE_RATIO) + "px")
            .style("font-weight", "bold")
            .style("font-family", this.fontFamily)
            .style("color",this.textColor)
            .style("fill",'currentColor')
            .html((stat) => { return "#" + stat.ranking + " (P" + stat.percentileValue + ")" });
      }

      //--------------------------
      // END Draw Lollipop Chart
      //--------------------------

      //-----------------------
      // Draw Bottom Left Text
      //-----------------------
      svg.append("text")
        .attr("x",- margin.left + 5)
        .attr("y", isMobileDevice ? ((this.svgHeight + (margin.bottom * 2.2)) * CHART_SIZE_RATIO) : CHART_HEIGHT)
        .attr("text-anchor", "start")
        .style("font-size", (cfg.labelFontSize * CHART_SIZE_RATIO) + "px")
        .style("font-family", this.fontFamily)
        .style("color",this.textColor)
        .style("fill",'currentColor')
        .text(this.dataPer90MinText);

      svg.append("text")
        .attr("x", - margin.left + 5)
        .attr("y", isMobileDevice ? ((this.svgHeight + (margin.bottom * 2.2) + 20) * CHART_SIZE_RATIO) : CHART_HEIGHT + 20)
        .attr("text-anchor", "start")
        .style("font-size", (cfg.labelFontSize * CHART_SIZE_RATIO) + "px")
        .style("font-family", this.fontFamily)
        .style("color", this.textColor)
        .style("fill",'currentColor')
        .text(this.adjustToPossessionText);

      //--------------
      // Draw Legend
      //-------------
      var legendPosition = {
        x: margin.left/32,
        y: isMobileDevice ? ((this.svgHeight + (margin.bottom * 2.1)) * CHART_SIZE_RATIO) : CHART_HEIGHT
      };
      var legend = svg.append("g");
      legend.selectAll("circle")
        .data(quintiles)
      .enter()
      .append("circle")
        .attr("cx", function(d, i) {
          if(isMobileDevice){
            if(i < 2){
              return legendPosition.x;
            }
            if(i < 4){
              return legendPosition.x + (cfg.legendOffsetX * CHART_SIZE_RATIO);
            }
            return legendPosition.x + (cfg.legendOffsetX * 2 * CHART_SIZE_RATIO);
          }else{
            return legendPosition.x + (cfg.legendOffsetX * CHART_SIZE_RATIO) * i;
          }
        })
        .attr("cy", function(d, i) {
          if(isMobileDevice){
            if(i < 2){
              return legendPosition.y + (cfg.legendOffsetY * CHART_SIZE_RATIO) * i;
            }
            if(i < 4){
              return legendPosition.y + (cfg.legendOffsetY * CHART_SIZE_RATIO) * (i - 2);
            }
            return legendPosition.y;
          }else{
            return legendPosition.y;
          }
        })
        .attr("r", (7 * CHART_SIZE_RATIO))
        .attr("fill", (cat) => { return cat.color });

      // Add Legend Labels
      legend.selectAll("text")
        .data(quintiles)
      .enter()
      .append("text")
        .attr("x", function(d, i) {
          if(isMobileDevice){
            if(i < 2){
              return legendPosition.x + (10 * CHART_SIZE_RATIO);
            }
            if(i < 4){
              return legendPosition.x + (cfg.legendOffsetX * CHART_SIZE_RATIO) + (10 * CHART_SIZE_RATIO);
            }
            return legendPosition.x + (cfg.legendOffsetX * 2 * CHART_SIZE_RATIO) + (10 * CHART_SIZE_RATIO);
          }else{
            return legendPosition.x + (cfg.legendOffsetX * CHART_SIZE_RATIO * i) + (10 * CHART_SIZE_RATIO);
          }
        })
        .attr("y", function(d, i) {
          if(isMobileDevice){
            if(i < 2){
              return legendPosition.y + (cfg.legendOffsetY * CHART_SIZE_RATIO * i) + (5 * CHART_SIZE_RATIO);
            }
            if(i < 4){
              return legendPosition.y + (cfg.legendOffsetY * CHART_SIZE_RATIO * (i - 2)) + (5 * CHART_SIZE_RATIO);
            }
            return legendPosition.y + (5 * CHART_SIZE_RATIO);
          }else{
            return legendPosition.y + (5 * CHART_SIZE_RATIO);
          }
        })
        .style("font-size", (cfg.legendFontSize * CHART_SIZE_RATIO) + "px")
        .style("font-family", this.fontFamily)
        .style("color",this.textColor)
        .style("fill",'currentColor')
        .text(function(cat) { return cat.value + "%" });

      //-------------------
      // Draw Driblab Logo
      //------------------
      if(!this.mediaMode){
        let base_image = new Image();
        let xLogo = isMobileDevice ? CHART_WIDTH - margin.left/8 : CHART_WIDTH - (130 * CHART_SIZE_RATIO);
        let yLogo = isMobileDevice ? CHART_HEIGHT - margin.top - logoHeight : (CHART_HEIGHT - margin.bottom/2);
        base_image.src = this.mediaMode ? require(this.logoCfg === 'white' ? "@/assets/img/driblab_normal_white.png" : "@/assets/img/driblab_normal_color.png") :
         require((this.logoCfg === 'white' || this.darkMode) ? "@/assets/img/driblab_white.png" : "@/assets/img/driblab_color.png");
        base_image.onload = (event) => {
          const dataUrl = getDataUrl(event.currentTarget);
          this.drawImage(
          svg,
          "driblab-logo",
          xLogo,
          yLogo,
          logoWidth,
          logoHeight,
          dataUrl,
          `${this.mediaMode}|${this.logoCfg}`
          );
        }
      }

      let out = svg.node().appendChild(document.createElement("defs"))
                    .appendChild(document.createElement("style"))
      out.setAttribute('type', 'text/css')
      out.appendChild(document.createTextNode(this.getLollipopStyles()));
      let font = await loadFonts(this.fontFamily);
      copyAndAppendElement(svg.node(),font.node(),0,0);

    },
    drawImage(svg, idLogo, x, y, width, height, img, id) {
      if(!this.images[id]){
        let imgNode = svg.append("svg:image")
          .attr("id", idLogo)
          .attr("x", x)
          .attr("y", y)
          .attr("width", width)
          .attr("height", height)
          .attr("xlink:href", img);
        this.images[id] = imgNode.node().cloneNode(true);
      }else{
        svg.node().appendChild(this.images[id]);
      }
    },
    getColorPercentile(percentileValue) {
      switch (true) {
        case percentileValue >= 80:
          return "#476F20";
        case percentileValue >= 60:
          return "#73B240";
        case percentileValue >= 40:
          return "#FCE230";
        case percentileValue >= 20:
          return "#FBA180";
        case percentileValue >= 0:
          return "#F64030";
        default:
          return "#C8C8C8";
      }
    },
    //Wraps SVG text
  	wrap(text, width) {
  	  text.each(function() {
    		var text = d3v4.select(this),
    			words = text.text().split(/\s+/).reverse(),
    			word,
    			line = [],
    			lineNumber = 0,
    			lineHeight = 0.85, //em
    			y = text.attr("y"),
    			x = text.attr("x"),
    			dy = parseFloat(text.attr("dy")),
    			tspan = text.text(null).append("tspan").attr("x", x).attr("y", y).attr("dy", dy + "em");

    		while (word = words.pop()) {
    		  line.push(word);
    		  tspan.text(line.join(" "));
    		  if (tspan.node().getComputedTextLength() > width) {
    				line.pop();
    				tspan.text(line.join(" "));
    				line = [word];
    				tspan = text.append("tspan").attr("x", x).attr("y", y).attr("dy", ++lineNumber * lineHeight + dy + "em").text(word);
    		  }
    		}
  	  });
  	},
    /* This function returns the size in pixels of the string */
  	measureText: function(string, fontSize = 10) {
  	  const widths = [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0.278125,0.278125,0.35625,0.55625,
  			0.55625,0.890625,0.6671875,0.1921875,0.334375,0.334375,0.390625,0.584375,0.278125,0.334375,0.278125,0.303125,0.55625,
  			0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.278125,0.278125,0.5859375,0.584375,0.5859375,
  			0.55625,1.015625,0.6671875,0.6671875,0.7234375,0.7234375,0.6671875,0.6109375,0.778125,0.7234375,0.278125,0.5,0.6671875,
  			0.55625,0.834375,0.7234375,0.778125,0.6671875,0.778125,0.7234375,0.6671875,0.6109375,0.7234375,0.6671875,0.9453125,
  			0.6671875,0.6671875,0.6109375,0.278125,0.35625,0.278125,0.478125,0.55625,0.334375,0.55625,0.55625,0.5,0.55625,
  			0.55625,0.278125,0.55625,0.55625,0.2234375,0.2421875,0.5,0.2234375,0.834375,0.55625,0.55625,0.55625,0.55625,
  			0.334375,0.5,0.278125,0.55625,0.5,0.7234375,0.5,0.5,0.5,0.35625,0.2609375,0.3546875,0.590625];
  	  const avg = 0.5293256578947368;

  	  return string
  	    .split('')
  	    .map(c => c.charCodeAt(0) < widths.length ? widths[c.charCodeAt(0)] : avg)
  	    .reduce((cur, acc) => acc + cur) * fontSize;
  	},
    getLollipopStyles(){
      return `.axisDark path,
      .axisDark line {
        fill: none;
        stroke: white;
        shape-rendering: crispEdges;
      }
      .axisDark text {
        fill: white;
      }`
    },
  },
  destroyed() {
    d3.select(`#${this.rootElementId}`).select("svg").remove();
    delete this.observer;
  },
}

</script>
<style lang="scss">
</style>
